import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox as _Checkbox,
  FormControlLabel as _FormControlLabel,
  Menu,
  MenuItem,
  Select,
  InputLabel as _InputLabel,
  FormControl,
  Divider,
  Chip,
  IconButton as _IconButton,
  InputAdornment as _InputAdornment,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ContentCopy, Check, ArrowForward } from '@mui/icons-material';
import {
  Notes as NotesIcon,
  Event as EventIcon,
  LocalOffer as TagIcon,
  AttachFile as AttachFileIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  PriorityHigh as PriorityIcon,
  Description as DescriptionIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import AddFacilityModal from '../components/AddFacilityModal';
import CaseTimeline from '../components/CaseTimeline';
import CaseDocuments from '../components/CaseDocuments';
import TemplateDocuments from '../components/TemplateDocuments';
import { CaseHeader } from '../components/case/CaseHeader';
import { InvestigativeTools } from '../components/case/InvestigativeTools';
import { DeleteFacilityDialog } from '../components/case/DeleteFacilityDialog';
import CaseTeam from '../components/case/CaseTeam';
import { ReviewFacilities } from '../components/case/ReviewFacilities';
import { CaseSummary } from '../components/case/CaseSummary';
import RichTextEditor from '../components/RichTextEditor';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Owner {
  id: string;
  name: string;
  type: string;
  address_line_1_owner?: string;
  city_owner?: string;
  state_owner?: string;
  zip_code_owner?: string;
  facilities_in_portfolio: number;
  role_suggestion: string;
  total_billed_in_portfolio: number;
  total_paid_in_portfolio: number;
  other_facilities: any[];
  other_portfolios: any[];
}

interface _Facility {
  ccn: string;
  org_name: string;
  dba_name?: string;
  city: string;
  state: string;
  total_billed: number;
  total_paid: number;
  owners: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      <Box sx={{ py: 3, display: value === index ? 'block' : 'none' }}>{children}</Box>
    </div>
  );
}

export default function Case() {
  const { schemeId, targetId, caseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, user, currentAccount } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  // Check if account is in demo mode
  const isDemoMode = currentAccount?.demo_mode ?? false;

  // Mapping functions to convert SNF data structure to frontend format
  const mapSNFOwners = (snfOwners: any[]) => {
    return snfOwners.map((owner: any) => ({
      id: owner.id,
      name: owner.name,
      type: owner.owner_type, // 'O' for Organization, 'I' for Individual
      address_line_1_owner: owner.address_line_1,
      city_owner: owner.city,
      state_owner: owner.state,
      zip_code_owner: owner.zip_code,
      facilities_in_portfolio: owner.num_facilities_in_case,
      role_suggestion: owner.role, // 'LEAD' or 'SECONDARY'
      total_billed_in_portfolio: parseFloat(owner.total_billed || 0),
      total_paid_in_portfolio: parseFloat(owner.total_paid || 0),
      other_facilities: owner.other_facilities || [],
      other_portfolios: owner.other_ownership_groups || [],
    }));
  };

  const mapSNFFacilities = (snfFacilities: any[]) => {
    return snfFacilities.map((facility: any) => ({
      ccn: facility.ccn,
      org_name: facility.org_name,
      dba_name: facility.dba_name,
      city: facility.city,
      state: facility.state,
      total_billed: parseFloat(facility.total_billed || 0),
      total_paid: parseFloat(facility.total_paid || 0),
      owners: facility.owners || [], // Now populated from API
    }));
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0); // 0 = Facilities tab (default)
  const [_selectedOwners, setSelectedOwners] = useState<Set<string>>(new Set());
  const [expandedOwners, setExpandedOwners] = useState<Set<string>>(new Set());
  const [selectedPortfoliosForAnalysis, setSelectedPortfoliosForAnalysis] = useState<Set<string>>(new Set());
  const [_consolidatedAnalysis, setConsolidatedAnalysis] = useState<any>(null);
  const [_networkExpanded, _setNetworkExpanded] = useState(false);
  const [facilitiesWithSchemeData, setFacilitiesWithSchemeData] = useState<any[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState<any>(null);
  const [addFacilityDialogOpen, setAddFacilityDialogOpen] = useState(false);
  const [expandedFacilitiesScheme, setExpandedFacilitiesScheme] = useState<Set<string>>(new Set());
  const [expandedSchemeInstances, setExpandedSchemeInstances] = useState<Set<string>>(new Set());
  const [highlightedOwner, setHighlightedOwner] = useState<string | null>(null);
  const [editingLeadDefendant, setEditingLeadDefendant] = useState(false);
  const [leadDefendantValue, setLeadDefendantValue] = useState('');
  const [editingCaseNumber, setEditingCaseNumber] = useState(false);
  const [caseNumberValue, setCaseNumberValue] = useState('');
  const [sortField, setSortField] = useState<string>('org_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedState] = useState<string>('');  // Keep for sortedFacilities dependency
  const [ownershipChangesData, setOwnershipChangesData] = useState<any>({});
  const [investigativeToolsLoaded, setInvestigativeToolsLoaded] = useState(false);
  const [investigativeToolsLoading, setInvestigativeToolsLoading] = useState(false);

  // Pagination state
  const [facilitiesData, setFacilitiesData] = useState<any>(null);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesPage, setFacilitiesPage] = useState(0);
  const [facilitiesPageSize, setFacilitiesPageSize] = useState(25);
  const [facilitiesSearch, setFacilitiesSearch] = useState('');
  const [facilitiesStateFilter, setFacilitiesStateFilter] = useState('');

  // Dropdown menu state
  const [newEntryMenuAnchor, setNewEntryMenuAnchor] = useState<null | HTMLElement>(null);
  const newEntryMenuOpen = Boolean(newEntryMenuAnchor);

  // Case log modal state
  const [caseLogModalOpen, setCaseLogModalOpen] = useState(false);
  const [newLogContent, setNewLogContent] = useState('');
  const [newLogDate, setNewLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [newLogTags, setNewLogTags] = useState('');
  const [newLogFiles, setNewLogFiles] = useState<File[]>([]);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [caseDocumentsRefreshKey, setCaseDocumentsRefreshKey] = useState(0);
  const [templateDocumentsRefreshKey, setTemplateDocumentsRefreshKey] = useState(0);

  // Task modal state
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskAssignedTo, setNewTaskAssignedTo] = useState('');
  const [newTaskFiles, setNewTaskFiles] = useState<File[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  // Copy email state
  const [emailCopied, setEmailCopied] = useState(false);

  // Next review state
  const [loadingNextReview, setLoadingNextReview] = useState(false);

  // Document generation task tracking
  const [docGenTaskId, setDocGenTaskId] = useState<string | null>(null);
  const [docGenStatus, setDocGenStatus] = useState<string>('');
  const [docGenRunning, setDocGenRunning] = useState(false);

  // Review statistics state
  const [reviewStats, setReviewStats] = useState<{
    total_cases: number;
    reviewed_cases: number;
    pending_reviews: number;
  } | null>(null);

  // Closure reason state
  const [closureReasonDialogOpen, setClosureReasonDialogOpen] = useState(false);
  const [closureReason, setClosureReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const _navigate = useNavigate();

  useEffect(() => {
    if (caseId) {
      fetchCaseData();
    } else if (schemeId && targetId) {
      fetchSchemeScopingData();
    }
  }, [schemeId, targetId, caseId]);

  // Fetch review statistics when case is IN_REVIEW
  useEffect(() => {
    if (caseId && data?.status === 'IN_REVIEW') {
      fetchReviewStats();
    }
  }, [caseId, data?.status]);

  // Handle URL parameters for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const highlightParam = searchParams.get('highlight');

    // If highlight parameter exists, go to documents tab
    if (highlightParam) {
      setTabValue(4); // Case Documents tab
    } else if (tabParam) {
      const tabMap: { [key: string]: number } = {
        'timeline': 0,
        'team': 1,
        'investigative': 2,
        'template': 3,
        'documents': 4
      };
      const tabIndex = tabMap[tabParam];
      if (tabIndex !== undefined) {
        setTabValue(tabIndex);
        // If landing on Investigative Tools tab, fetch the data
        if (tabIndex === 2 && caseId && data) {
          fetchInvestigativeToolsData();
        }
      }
    }
  }, [searchParams, caseId, data]);

  // Handle document generation task tracking from URL params
  useEffect(() => {
    const taskIdParam = searchParams.get('task_id');
    if (taskIdParam && !docGenTaskId) {
      setDocGenTaskId(taskIdParam);
      setDocGenRunning(true);
      setDocGenStatus('Generating documents...');

      // Start polling for task status
      const pollInterval = setInterval(async () => {
        try {
          const { data: taskData } = await api.get(`/celery-tasks/${taskIdParam}/`, { headers });

          setDocGenStatus(taskData.status_message || 'Generating documents...');

          if (taskData.state === 'SUCCESS') {
            clearInterval(pollInterval);
            setDocGenRunning(false);
            setDocGenStatus('Documents generated successfully!');
            // Remove task_id from URL
            searchParams.delete('task_id');
            setSearchParams(searchParams, { replace: true });
            // Refresh the documents tables
            setTemplateDocumentsRefreshKey(prev => prev + 1);
            setCaseDocumentsRefreshKey(prev => prev + 1);
            // Clear success message after a few seconds
            setTimeout(() => {
              setDocGenTaskId(null);
              setDocGenStatus('');
            }, 3000);
          } else if (taskData.state === 'FAILURE') {
            clearInterval(pollInterval);
            setDocGenRunning(false);
            setDocGenStatus('Document generation failed');
            // Remove task_id from URL
            searchParams.delete('task_id');
            setSearchParams(searchParams, { replace: true });
          }
        } catch (err) {
          console.error('Failed to poll task status:', err);
          clearInterval(pollInterval);
          setDocGenRunning(false);
          setDocGenTaskId(null);
        }
      }, 2000);

      // Cleanup on unmount
      return () => clearInterval(pollInterval);
    }
  }, [searchParams, headers]);

  // Trigger paginated fetch when pagination state changes (only if Investigative Tools tab is active and data is loaded)
  useEffect(() => {
    if (caseId && data && investigativeToolsLoaded) {
      fetchPaginatedFacilities();
    }
  }, [facilitiesPage, facilitiesPageSize, facilitiesSearch, facilitiesStateFilter, sortField, sortDirection, investigativeToolsLoaded]);

  // Initial pagination fetch when Investigative Tools tab is opened
  useEffect(() => {
    if (caseId && data && !facilitiesData && investigativeToolsLoaded) {
      fetchPaginatedFacilities();
    }
  }, [caseId, data, investigativeToolsLoaded]);


  // New function for paginated facilities data
  const fetchPaginatedFacilities = async () => {
    if (!caseId) return;

    setFacilitiesLoading(true);
    try {
      const params = new URLSearchParams({
        case_id: caseId,
        include_instances: 'true',
        page: (facilitiesPage + 1).toString(), // API uses 1-based pagination, UI uses 0-based
        page_size: facilitiesPageSize.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
      });

      if (facilitiesSearch.trim()) {
        params.append('search', facilitiesSearch.trim());
      }

      if (facilitiesStateFilter) {
        params.append('state', facilitiesStateFilter);
      }

      const response = await api.get(`/snfs/facilities/?${params.toString()}`, { headers });

      // Handle both old and new response formats
      if (response.data.results) {
        // New paginated format
        setFacilitiesData(response.data);

        // Ensure the scheme data preserves the original facility financial data
        const enhancedFacilities = response.data.results.map((facilityWithScheme: any) => {
          const originalFacility = data.facilities?.find((f: any) => f.ccn === facilityWithScheme.ccn);
          return {
            ...facilityWithScheme,
            // Preserve original financial data and other fields
            total_billed: originalFacility?.total_billed || facilityWithScheme.total_billed,
            total_paid: originalFacility?.total_paid || facilityWithScheme.total_paid,
            owners: facilityWithScheme.owners || originalFacility?.owners || []
          };
        });

        setFacilitiesWithSchemeData(enhancedFacilities);
      } else {
        // Old format fallback
        setFacilitiesData({
          results: response.data,
          total_count: response.data.length,
          page: facilitiesPage + 1,
          page_size: facilitiesPageSize,
          total_pages: 1
        });
        setFacilitiesWithSchemeData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch paginated facilities:', error);
    } finally {
      setFacilitiesLoading(false);
    }
  };


  const fetchCaseData = async () => {
    setLoading(true);
    setInvestigativeToolsLoading(true);
    try {
      const caseResponse = await api.get(`/cases/${caseId}/`, { headers });
      const caseData = caseResponse.data;

      // Map basic case data without SNF scoping data (loaded separately for Investigative Tools)
      const enrichedData: any = {
        id: caseData.id,
        name: caseData.name,
        lead_defendant: caseData.lead_defendant,
        case_number: caseData.case_number,
        status: caseData.status,
        scheme: caseData.scheme,
        owners: mapSNFOwners(caseData.owners || []),
        facilities: mapSNFFacilities(caseData.facilities || []),
        origin_target: caseData.origin_target,
        related_cases: caseData.related_cases || [],
        related_targets: caseData.related_targets || []
      };

      // Set the core data immediately so UI can start rendering
      setData(enrichedData);
      setLeadDefendantValue(enrichedData.lead_defendant || '');
      setCaseNumberValue(caseData.case_number || '');

      // Auto-select the first LEAD owner
      if (enrichedData.owners?.length > 0) {
        const leadOwner = enrichedData.owners.find((owner: Owner) => owner.role_suggestion === 'LEAD');
        if (leadOwner) {
          setSelectedOwners(new Set([leadOwner.id]));
        }
      }

      // If status is IN_REVIEW, load investigative tools data immediately for the Review tab
      if (enrichedData.status === 'IN_REVIEW') {
        await fetchInvestigativeToolsData();
      } else {
        // DEMO EDIT (2026-05-20): Timeline/Team tabs hidden — default to the Investigative Tools tab (index 2).
        // Remove this else branch after the demo to restore the Timeline tab as the default.
        if (!searchParams.get('tab') && !searchParams.get('highlight')) {
          setTabValue(2);
        }
        await fetchInvestigativeToolsData();
      }

    } catch (error) {
      console.error('Error fetching case data:', error);
    } finally {
      setLoading(false);
      setInvestigativeToolsLoading(false);
    }
  };

  const fetchInvestigativeToolsData = async () => {
    if (!caseId || investigativeToolsLoaded) {
      return;
    }

    setInvestigativeToolsLoading(true);
    try {
      const snfScopingResponse = await api.get(`/snfs/scoping/?case_id=${caseId}`, { headers });
      const ownershipResponse = await api.get(`/snfs/facilities/ownership_changes/?case_id=${caseId}`, { headers });

      const snfData = snfScopingResponse.data;

      // Update data with SNF scoping information
      setData((prevData: any) => ({
        ...prevData,
        total_billed: snfData.total_billed,
        total_paid: snfData.total_paid,
        scheme_instance_summary: snfData.scheme_instance_summary,
        summary: snfData.summary || {},
        owners: mapSNFOwners(snfData.owners || []),
        facilities: mapSNFFacilities(snfData.facilities || [])
      }));

      // Don't overwrite facilitiesWithSchemeData here - it should be populated by fetchPaginatedFacilities
      // which includes scheme instances. The scoping endpoint doesn't have scheme instances.
      setOwnershipChangesData(ownershipResponse.data || {});
      setInvestigativeToolsLoaded(true);

      // Auto-select the first LEAD owner from SNF data
      const leadOwner = snfData.owners?.find((owner: any) => owner.role_suggestion === 'LEAD');
      if (leadOwner) {
        const mappedOwners = mapSNFOwners(snfData.owners || []);
        const leadOwnerMapped = mappedOwners.find((o: Owner) => o.role_suggestion === 'LEAD');
        if (leadOwnerMapped) {
          setSelectedOwners(new Set([leadOwnerMapped.id]));
        }
      }
    } catch (err) {
      console.error('Failed to fetch investigative tools data:', err);
      setOwnershipChangesData({});
    } finally {
      setInvestigativeToolsLoading(false);
    }
  };

  const fetchSchemeScopingData = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/schemes/${schemeId}/targets/${targetId}/case_scoping/`,
        { headers }
      );


      console.log('Case scoping data:', response.data); // Debug log
      setData(response.data);
      setLeadDefendantValue(response.data.lead_defendant || response.data.name || '');


      // Auto-select the first LEAD owner
      if (response.data.owners?.length > 0) {
        const leadOwner = response.data.owners.find((owner: Owner) => owner.role_suggestion === 'LEAD');
        if (leadOwner) {
          setSelectedOwners(new Set([leadOwner.id]));
        }
      }
    } catch (error) {
      console.error('Error fetching case scoping data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewStats = async () => {
    try {
      const res = await api.get('/review-list/', {
        headers,
        params: {
          page: 1,
          page_size: 1,
        }
      });

      if (res.data.stats) {
        setReviewStats(res.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch review stats:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);

    // If navigating to Review tab when status is IN_REVIEW (index 0 in this case), load the data
    if (newValue === 0 && caseId && data?.status === 'IN_REVIEW') {
      fetchInvestigativeToolsData();
    }

    // If navigating to Investigative Tools tab (index 2), load the data
    if (newValue === 2 && caseId && data?.status !== 'IN_REVIEW') {
      fetchInvestigativeToolsData();
    }

    // If navigating away from documents tab and highlight parameter exists, clear it
    if (tabValue === 4 && newValue !== 4 && searchParams.get('highlight')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('highlight');
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  };

  const handleSaveLeadDefendant = async () => {
    if (!caseId || !leadDefendantValue.trim()) return;
    
    try {
      await api.patch(`/cases/${caseId}/`, {
        lead_defendant: leadDefendantValue.trim()
      }, { headers });
      
      // Update the data in state
      setData((prev: any) => ({
        ...prev,
        lead_defendant: leadDefendantValue.trim()
      }));
      
      setEditingLeadDefendant(false);
    } catch (error) {
      console.error('Failed to update lead defendant:', error);
    }
  };

  const handleCancelEditLeadDefendant = () => {
    setLeadDefendantValue(data?.lead_defendant || '');
    setEditingLeadDefendant(false);
  };

  const handleSaveCaseNumber = async () => {
    if (!caseId) return;

    try {
      await api.patch(`/cases/${caseId}/`, {
        case_number: caseNumberValue.trim()
      }, { headers });

      // Update the data in state
      setData((prev: any) => ({
        ...prev,
        case_number: caseNumberValue.trim()
      }));

      setEditingCaseNumber(false);
    } catch (error) {
      console.error('Failed to update case number:', error);
    }
  };

  const handleCancelEditCaseNumber = () => {
    setCaseNumberValue(data?.case_number || '');
    setEditingCaseNumber(false);
  };

  const handleStatusChange = async (status: string) => {
    if (!caseId) return;

    // If changing to CLOSED, prompt for reason
    if (status === 'CLOSED') {
      setPendingStatus(status);
      setClosureReasonDialogOpen(true);
      return;
    }

    // For other statuses, update directly
    try {
      await api.patch(`/cases/${caseId}/`, {
        status: status
      }, { headers });

      // Update the data in state
      setData((prev: any) => ({
        ...prev,
        status: status
      }));
    } catch (error) {
      console.error('Failed to update case status:', error);
    }
  };

  const handleClosureReasonSubmit = async () => {
    if (!caseId || !closureReason.trim()) return;

    try {
      // Update the status
      await api.patch(`/cases/${caseId}/`, {
        status: pendingStatus
      }, { headers });

      // Create a case log entry with the closure reason
      const formData = new FormData();
      formData.append('content', `Case closed. Reason: ${closureReason.trim()}`);
      formData.append('log_date', new Date().toISOString());
      formData.append('tags', 'closure');

      await api.post(`/cases/${caseId}/log/`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update the data in state
      setData((prev: any) => ({
        ...prev,
        status: pendingStatus
      }));

      // Refresh timeline to show the closure log
      setTimelineRefreshKey(prev => prev + 1);

      // Close dialog and reset
      setClosureReasonDialogOpen(false);
      setClosureReason('');
      setPendingStatus(null);
    } catch (error) {
      console.error('Failed to close case:', error);
    }
  };

  const handleClosureReasonCancel = () => {
    setClosureReasonDialogOpen(false);
    setClosureReason('');
    setPendingStatus(null);
  };

  const handleOwnerExpand = (ownerId: string) => {
    setExpandedOwners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerId)) {
        newSet.delete(ownerId);
      } else {
        newSet.add(ownerId);
      }
      return newSet;
    });
  };

  const _handlePortfolioToggle = (portfolioId: string) => {
    setSelectedPortfoliosForAnalysis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(portfolioId)) {
        newSet.delete(portfolioId);
      } else {
        newSet.add(portfolioId);
      }
      return newSet;
    });
  };

  const handleOwnerPillClick = (ownerName: string) => {
    if (highlightedOwner === ownerName) {
      setHighlightedOwner(null); // Deselect if same owner is clicked
    } else {
      setHighlightedOwner(ownerName);
    }
  };

  const handleFacilitySchemeExpand = (ccn: string) => {
    setExpandedFacilitiesScheme(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ccn)) {
        newSet.delete(ccn);
      } else {
        newSet.add(ccn);
      }
      return newSet;
    });
  };

  const handleSchemeInstanceExpand = (instanceKey: string) => {
    setExpandedSchemeInstances(prev => {
      const newSet = new Set(prev);
      if (newSet.has(instanceKey)) {
        newSet.delete(instanceKey);
      } else {
        newSet.add(instanceKey);
      }
      return newSet;
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleDeleteFacilityClick = (facility: any) => {
    setFacilityToDelete(facility);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteFacility = async () => {
    if (!caseId || !facilityToDelete) return;

    try {
      await api.delete(`/cases/${caseId}/facilities/${facilityToDelete.ccn}/`, { headers });

      // Close dialog first
      setDeleteConfirmOpen(false);
      setFacilityToDelete(null);

      // Reload all case data using the same method as initial load
      await fetchCaseData();
      // Refresh investigative tools data if already loaded
      if (investigativeToolsLoaded) {
        setInvestigativeToolsLoaded(false);
        await fetchInvestigativeToolsData();
      }
      // Refresh the facilities table
      await fetchPaginatedFacilities();
    } catch (error) {
      console.error('Failed to delete facility:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setFacilityToDelete(null);
  };

  // Add Facility handlers
  const handleAddFacilityDialogOpen = () => {
    setAddFacilityDialogOpen(true);
  };

  const handleAddFacilityDialogClose = () => {
    setAddFacilityDialogOpen(false);
  };

  // Dropdown menu handlers
  const _handleOpenNewEntryMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNewEntryMenuAnchor(event.currentTarget);
  };

  const handleCloseNewEntryMenu = () => {
    setNewEntryMenuAnchor(null);
  };

  // Case log handlers
  const handleOpenCaseLogModal = () => {
    handleCloseNewEntryMenu();
    setCaseLogModalOpen(true);
  };

  const handleCloseCaseLogModal = () => {
    setCaseLogModalOpen(false);
    setNewLogContent('');
    setNewLogDate(new Date().toISOString().split('T')[0]);
    setNewLogTags('');
    setNewLogFiles([]);
  };

  const handleCreateCaseLog = async () => {
    if (!caseId || !newLogContent.trim()) return;

    try {
      const formData = new FormData();
      formData.append('content', newLogContent.trim());
      formData.append('log_date', new Date(newLogDate).toISOString());
      formData.append('tags', newLogTags);

      // Add files if any
      newLogFiles.forEach((file) => {
        formData.append('attachment_files', file);
      });

      await api.post(`/cases/${caseId}/log/`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      handleCloseCaseLogModal();
      // Refresh timeline to show the new entry
      setTimelineRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to create case log:', error);
    }
  };

  // Task handlers
  const handleOpenTaskModal = async () => {
    handleCloseNewEntryMenu();
    // Fetch available users for assignment
    try {
      const response = await api.get('/users/', { headers });
      setAvailableUsers(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
    setTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setTaskModalOpen(false);
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskDueDate('');
    setNewTaskPriority('medium');
    setNewTaskAssignedTo('');
    setNewTaskFiles([]);
  };

  const handleCreateTask = async () => {
    if (!caseId || !newTaskTitle.trim()) return;

    try {
      // Ensure description is a string (could be object or string from editor)
      const descriptionToSend = typeof newTaskDescription === 'string'
        ? newTaskDescription
        : JSON.stringify(newTaskDescription);

      // Use FormData if there are files to upload
      if (newTaskFiles.length > 0) {
        const formData = new FormData();
        formData.append('title', newTaskTitle.trim());
        formData.append('description', descriptionToSend);
        formData.append('priority', newTaskPriority);
        formData.append('case', caseId);

        if (newTaskDueDate) {
          formData.append('due_date', new Date(newTaskDueDate).toISOString());
        }

        if (newTaskAssignedTo) {
          formData.append('assigned_to', newTaskAssignedTo);
        }

        // Append all files
        newTaskFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });

        await api.post('/tasks/', formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use JSON payload when no files
        const payload: any = {
          title: newTaskTitle.trim(),
          description: descriptionToSend,
          priority: newTaskPriority,
          case: caseId,
        };

        if (newTaskDueDate) {
          payload.due_date = new Date(newTaskDueDate).toISOString();
        }

        if (newTaskAssignedTo) {
          payload.assigned_to = newTaskAssignedTo;
        }

        await api.post('/tasks/', payload, { headers });
      }

      handleCloseTaskModal();
      // Refresh timeline to show the new task
      setTimelineRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleAddFacility = async (ccn: string) => {
    if (!caseId || !ccn) return;

    try {
      await api.post(`/cases/${caseId}/facilities/`, {
        ccn: ccn
      }, { headers });

      // Reload case data to show the new facility
      await fetchCaseData();
      // Refresh investigative tools data if already loaded
      if (investigativeToolsLoaded) {
        setInvestigativeToolsLoaded(false);
        await fetchInvestigativeToolsData();
      }
      // Refresh the facilities table
      await fetchPaginatedFacilities();
    } catch (error) {
      console.error('Failed to add facility:', error);
    }
  };

  // Handle document moved from template to case files
  const handleDocumentMove = () => {
    setCaseDocumentsRefreshKey(prev => prev + 1);
    setTimelineRefreshKey(prev => prev + 1);
  };

  // Filtered and sorted facilities data - MUST be before early returns
  const sortedFacilities = useMemo(() => {
    // Use paginated data when available, otherwise fall back to original data
    let facilitiesToSort;
    if (facilitiesData && facilitiesData.results) {
      // Use paginated results (already filtered and sorted by backend)
      facilitiesToSort = facilitiesWithSchemeData.length > 0 ? facilitiesWithSchemeData : facilitiesData.results;
    } else {
      // Fallback to original data for non-paginated view
      facilitiesToSort = facilitiesWithSchemeData.length > 0 ? facilitiesWithSchemeData : data?.facilities;
    }

    if (!facilitiesToSort) return facilitiesToSort;

    // Don't apply state filtering here when using paginated data since backend handles it
    if (!facilitiesData?.results && selectedState) {
      facilitiesToSort = facilitiesToSort.filter((facility: any) =>
        facility.state === selectedState
      );
    }

    // Apply sorting
    if (!sortField) return facilitiesToSort;

    return [...facilitiesToSort].sort((a, b) => {
      let aVal, bVal;

      if (sortField === 'total_billed' || sortField === 'total_paid') {
        aVal = a[sortField] || 0;
        bVal = b[sortField] || 0;
      } else if (sortField === 'org_name') {
        aVal = a.org_name || '';
        bVal = b.org_name || '';
      } else {
        aVal = a[sortField] || '';
        bVal = b[sortField] || '';
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [facilitiesWithSchemeData, data?.facilities, facilitiesData, sortField, sortDirection, selectedState]);

  const _calculateConsolidatedAnalysis = () => {
    if (!data?.owners) return;

    // Use a Map to deduplicate facilities by CCN
    const facilitiesMap = new Map();
    const consolidatedOwners = new Map();
    
    // Add current portfolio facilities
    data.facilities?.forEach((facility: any) => {
      facilitiesMap.set(facility.ccn, {
        ...facility,
        portfolios: [data.name],
        all_owners: [...(facility.owners || [])]
      });
    });

    // Add current portfolio owners
    data.owners.forEach((owner: Owner) => {
      consolidatedOwners.set(owner.id, {
        ...owner,
        total_facilities: 0, // Will recalculate properly
        total_billed_across_portfolios: 0, // Will recalculate properly  
        total_paid_across_portfolios: 0, // Will recalculate properly
        involved_portfolios: [data.name]
      });
    });

    // Process selected additional portfolios
    data.owners?.forEach((owner: Owner) => {
      owner.other_portfolios?.forEach((portfolio: any) => {
        if (selectedPortfoliosForAnalysis.has(portfolio.portfolio_uuid)) {
          // Add/update owner data
          const consolidatedOwner = consolidatedOwners.get(owner.id);
          if (consolidatedOwner) {
            if (!consolidatedOwner.involved_portfolios.includes(portfolio.scheme_target_name)) {
              consolidatedOwner.involved_portfolios.push(portfolio.scheme_target_name);
            }
          }

          // Add facilities from this portfolio (deduplicated)
          portfolio.facility_details?.forEach((facility: any) => {
            const existingFacility = facilitiesMap.get(facility.ccn);
            if (existingFacility) {
              // Facility already exists, just add portfolio and owner if not already there
              if (!existingFacility.portfolios.includes(portfolio.scheme_target_name)) {
                existingFacility.portfolios.push(portfolio.scheme_target_name);
              }
              if (!existingFacility.all_owners.includes(owner.name)) {
                existingFacility.all_owners.push(owner.name);
              }
            } else {
              // New facility, add it
              facilitiesMap.set(facility.ccn, {
                ccn: facility.ccn,
                org_name: facility.name,
                state: facility.state,
                total_billed: 0, // We don't have individual facility billing from other portfolios
                total_paid: 0,
                owners: [owner.name],
                portfolios: [portfolio.scheme_target_name],
                all_owners: [owner.name]
              });
            }
          });
        }
      });
    });

    // Now properly calculate owner metrics based on unique facilities
    Array.from(consolidatedOwners.values()).forEach((owner: any) => {
      let ownerFacilities = 0;
      let ownerBilled = 0;
      let ownerPaid = 0;

      // Count facilities where this owner is involved and calculate proper financial totals
      const ownerFacilitiesInCurrentPortfolio = new Set();
      
      Array.from(facilitiesMap.values()).forEach((facility: any) => {
        if (facility.all_owners.includes(owner.name)) {
          ownerFacilities += 1;
          
          // If facility is in current portfolio, get actual amounts
          if (facility.portfolios.includes(data.name)) {
            ownerFacilitiesInCurrentPortfolio.add(facility.ccn);
            ownerBilled += facility.total_billed || 0;
            ownerPaid += facility.total_paid || 0;
          }
        }
      });

      // SIMPLIFIED: Don't estimate amounts from other portfolios for owners either
      // This avoids the same double-counting problem at the owner level

      owner.total_facilities = ownerFacilities;
      owner.total_billed_across_portfolios = ownerBilled;
      owner.total_paid_across_portfolios = ownerPaid;
    });

    const consolidatedFacilities = Array.from(facilitiesMap.values());
    
    // Calculate totals from unique facilities only (no double-counting)
    let totalBilled = 0;
    let totalPaid = 0;
    
    // Only count amounts from current portfolio facilities (we have exact amounts)
    consolidatedFacilities.forEach((facility: any) => {
      if (facility.portfolios.includes(data.name)) {
        totalBilled += facility.total_billed || 0;
        totalPaid += facility.total_paid || 0;
      }
    });
    
    // SIMPLIFIED APPROACH: Don't estimate amounts for other portfolio facilities
    // This avoids the complex estimation that was leading to inflated numbers
    // We'll only count actual amounts from the current portfolio
    
    // Note: For facilities that appear only in other portfolios, we can't reliably 
    // estimate their financial contribution without facility-level data from those portfolios
    
    // Debug logging
    console.log('Portfolio Consolidation Debug:', {
      totalBilledFromCurrentPortfolio: totalBilled,
      totalFacilities: consolidatedFacilities.length,
      currentPortfolioFacilities: consolidatedFacilities.filter(f => f.portfolios.includes(data.name)).length,
      facilitiesFromOtherPortfoliosOnly: consolidatedFacilities.filter(f => !f.portfolios.includes(data.name)).length,
      selectedAdditionalPortfolios: selectedPortfoliosForAnalysis.size
    });
    
    const analysis = {
      total_facilities: consolidatedFacilities.length,
      total_owners: consolidatedOwners.size,
      total_billed: totalBilled,
      total_paid: totalPaid,
      states: Array.from(new Set(consolidatedFacilities.map(f => f.state).filter(Boolean))),
      owners: Array.from(consolidatedOwners.values()),
      facilities: consolidatedFacilities,
      selected_portfolios: Array.from(selectedPortfoliosForAnalysis).length + 1 // +1 for current
    };

    setConsolidatedAnalysis(analysis);
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <Alert severity="error">Failed to load case scoping data</Alert>
      </Layout>
    );
  }

  const formatMoney = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '$0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return `$${(numValue || 0).toLocaleString()}`;
  };

  // Get email domain based on environment
  const getEmailDomain = () => {
    const hostname = window.location.hostname;
    // Production: app.eliotnest.com
    // Everything else (dev, localhost): cases-dev.eliotnest.com
    return hostname === 'app.eliotnest.com'
      ? 'cases.eliotnest.com'
      : 'cases-dev.eliotnest.com';
  };

  // Generate case email address
  const caseEmail = caseId ? `case-${caseId}@${getEmailDomain()}` : '';

  // Copy email to clipboard
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(caseEmail);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  // Navigate to next pending review case
  const handleNextReview = async () => {
    setLoadingNextReview(true);
    try {
      const res = await api.get('/review-list/', {
        headers,
        params: {
          page: 1,
          page_size: 1,
          has_reviewed: 'false',  // Only get cases that haven't been reviewed yet
          ordering: '-total_billed',  // Order by total_billed desc
        }
      });
      const cases = res.data.results ?? res.data;

      if (cases && cases.length > 0) {
        // Force full page reload to reset all state
        window.location.href = `/cases/${cases[0].id}`;
      } else {
        // No pending reviews left, could show a message or navigate to review list
        console.log('No pending reviews remaining');
        window.location.href = '/reviews';
      }
    } catch (err) {
      console.error('Failed to fetch next review:', err);
      setLoadingNextReview(false);
    }
    // Note: setLoadingNextReview(false) not needed in finally since we're doing a full page reload
  };

  return (
    <Layout>
      {/* Document Generation Progress Banner */}
      {(docGenRunning || docGenStatus) && (
        <Alert
          severity={docGenRunning ? 'info' : (docGenStatus.includes('failed') ? 'error' : 'success')}
          sx={{ mb: 2 }}
          icon={docGenRunning ? <CircularProgress size={20} /> : undefined}
        >
          <Typography variant="body2">
            {docGenStatus}
          </Typography>
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        {/* Main Content with Header and Tabs */}
        <Paper sx={{ width: '100%' }}>
          {/* Header Section */}
          <CaseHeader
            data={data}
            caseId={caseId}
            editingLeadDefendant={editingLeadDefendant}
            leadDefendantValue={leadDefendantValue}
            onEditLeadDefendant={setEditingLeadDefendant}
            onLeadDefendantChange={setLeadDefendantValue}
            onSaveLeadDefendant={handleSaveLeadDefendant}
            onCancelEditLeadDefendant={handleCancelEditLeadDefendant}
            editingCaseNumber={editingCaseNumber}
            caseNumberValue={caseNumberValue}
            onEditCaseNumber={setEditingCaseNumber}
            onCaseNumberChange={setCaseNumberValue}
            onSaveCaseNumber={handleSaveCaseNumber}
            onCancelEditCaseNumber={handleCancelEditCaseNumber}
            onStatusChange={handleStatusChange}
            userRole={user?.role}
            formatMoney={formatMoney}
            demoMode={isDemoMode}
          />

          {/* Case Summary - shown above tabs in demo mode */}
          {isDemoMode && data.status === 'IN_REVIEW' && caseId && (
            <CaseSummary caseId={caseId} demoMode={true} />
          )}

          {/* Tabs Section */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            py: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                flexGrow: 1,
                px: 2,
                '& .MuiTabs-indicator': {
                  height: 3,
                },
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  px: 3,
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: '8px 8px 0 0',
                  },
                },
              }}
            >
              {data.status === 'IN_REVIEW' ? (
                isDemoMode ? [
                  <Tab key="documents" label="Documents" />,
                  <Tab key="visualize" label="Visualize" />
                ] : [
                  <Tab key="review" label="Review" />
                ]
              ) : [
                /* DEMO EDIT (2026-05-20): Timeline & Team tabs hidden for demo — restore the two <Tab> lines below
                   (and drop the explicit value={} props) after the demo.
                <Tab key="timeline" label="Timeline" />,
                <Tab key="team" label="Team" />,
                */
                <Tab key="investigative" label="Investigative Tools" value={2} />,
                <Tab key="template" label="Template Documents" value={3} />,
                <Tab key="documents" label="Case Documents" value={4} />
              ]}
            </Tabs>
            {/* Review Stats and Next Review Button when case is IN_REVIEW (hidden in demo mode) | Copy Email Button otherwise */}
            {data.status === 'IN_REVIEW' && !isDemoMode ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 3 }}>
                {reviewStats && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Review Summary:
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 2,
                      py: 0.75,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Completed:
                        </Typography>
                        <Chip
                          label={reviewStats.reviewed_cases}
                          size="small"
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Pending:
                        </Typography>
                        <Chip
                          label={reviewStats.pending_reviews}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
                <Button
                  variant="contained"
                  size="medium"
                  endIcon={loadingNextReview ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                  onClick={handleNextReview}
                  disabled={loadingNextReview}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {loadingNextReview ? 'Loading...' : 'Next Review'}
                </Button>
              </Box>
            ) : data.status !== 'IN_REVIEW' ? (
              caseId && caseEmail && (
                <Tooltip title={emailCopied ? `Copied: ${caseEmail}` : `Copy case email: ${caseEmail}`}>
                  <Button
                    size="small"
                    startIcon={emailCopied ? <Check /> : <ContentCopy />}
                    onClick={handleCopyEmail}
                    sx={{
                      color: emailCopied ? 'success.main' : 'text.secondary',
                      textTransform: 'none',
                      fontSize: '0.8125rem',
                      minWidth: 'auto',
                      mr: 3,
                      '&:hover': {
                        color: emailCopied ? 'success.main' : 'primary.main',
                        bgcolor: emailCopied ? 'success.light' : 'action.hover',
                      },
                    }}
                  >
                    {emailCopied ? 'Copied!' : 'Copy Email'}
                  </Button>
                </Tooltip>
              )
            ) : null}
            {/* New Entry button - commented out in favor of inline toggle creator in timeline */}
            {/* <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleOpenNewEntryMenu}
              size="medium"
              sx={{
                mr: 3,
                ml: 2,
                textTransform: 'none',
              }}
            >
              New Entry
            </Button> */}
            <Menu
              anchorEl={newEntryMenuAnchor}
              open={newEntryMenuOpen}
              onClose={handleCloseNewEntryMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  minWidth: 200,
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <MenuItem
                onClick={handleOpenCaseLogModal}
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  '&:hover': {
                    backgroundColor: 'primary.50',
                  }
                }}
              >
                <NotesIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Log Entry
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={handleOpenTaskModal}
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  '&:hover': {
                    backgroundColor: 'primary.50',
                  }
                }}
              >
                <AssignmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  New Task
                </Typography>
              </MenuItem>
            </Menu>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {data.status === 'IN_REVIEW' ? (
              isDemoMode ? (
                /* Documents Tab (demo mode: first tab shows TemplateDocuments) */
                caseId && <TemplateDocuments key={templateDocumentsRefreshKey} caseId={caseId} caseData={data} onDocumentMove={handleDocumentMove} reviewMode={true} />
              ) : (
                /* Review Tab (normal IN_REVIEW mode) */
                <ReviewFacilities
                  caseId={caseId}
                  caseData={data}
                  scopingData={{
                    facilities: data.facilities || [],
                    owners: data.owners || [],
                    summary: data.summary || {}
                  }}
                  facilities={sortedFacilities || []}
                  facilitiesWithSchemeData={facilitiesWithSchemeData}
                  facilitiesData={facilitiesData}
                  facilitiesLoading={facilitiesLoading}
                  facilitiesSearch={facilitiesSearch}
                  facilitiesStateFilter={facilitiesStateFilter}
                  facilitiesPage={facilitiesPage}
                  facilitiesPageSize={facilitiesPageSize}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  expandedFacilitiesScheme={expandedFacilitiesScheme}
                  expandedSchemeInstances={expandedSchemeInstances}
                  highlightedOwner={highlightedOwner}
                  states={data.summary?.states || []}
                  ownershipChangesData={ownershipChangesData}
                  onSearchChange={(value) => {
                    setFacilitiesSearch(value);
                    setFacilitiesPage(0);
                  }}
                  onStateFilterChange={(value) => {
                    setFacilitiesStateFilter(value);
                    setFacilitiesPage(0);
                  }}
                  onPageChange={setFacilitiesPage}
                  onPageSizeChange={setFacilitiesPageSize}
                  onSort={handleSort}
                  onFacilitySchemeExpand={handleFacilitySchemeExpand}
                  onSchemeInstanceExpand={handleSchemeInstanceExpand}
                  onOwnerPillClick={handleOwnerPillClick}
                  owners={data.owners || []}
                  formatMoney={formatMoney}
                />
              )
            ) : (
              /* Timeline Tab (when not IN_REVIEW) */
              caseId && <CaseTimeline key={timelineRefreshKey} caseId={caseId} />
            )}
          </TabPanel>

          {/* Visualize Tab (demo mode only - shows facilities without CaseSummary and CaseReviewForm) */}
          {data.status === 'IN_REVIEW' && isDemoMode && (
            <TabPanel value={tabValue} index={1}>
              <ReviewFacilities
                caseId={caseId}
                caseData={data}
                scopingData={{
                  facilities: data.facilities || [],
                  owners: data.owners || [],
                  summary: data.summary || {}
                }}
                facilities={sortedFacilities || []}
                facilitiesWithSchemeData={facilitiesWithSchemeData}
                facilitiesData={facilitiesData}
                facilitiesLoading={facilitiesLoading}
                facilitiesSearch={facilitiesSearch}
                facilitiesStateFilter={facilitiesStateFilter}
                facilitiesPage={facilitiesPage}
                facilitiesPageSize={facilitiesPageSize}
                sortField={sortField}
                sortDirection={sortDirection}
                expandedFacilitiesScheme={expandedFacilitiesScheme}
                expandedSchemeInstances={expandedSchemeInstances}
                highlightedOwner={highlightedOwner}
                states={data.summary?.states || []}
                ownershipChangesData={ownershipChangesData}
                onSearchChange={(value) => {
                  setFacilitiesSearch(value);
                  setFacilitiesPage(0);
                }}
                onStateFilterChange={(value) => {
                  setFacilitiesStateFilter(value);
                  setFacilitiesPage(0);
                }}
                onPageChange={setFacilitiesPage}
                onPageSizeChange={setFacilitiesPageSize}
                onSort={handleSort}
                onFacilitySchemeExpand={handleFacilitySchemeExpand}
                onSchemeInstanceExpand={handleSchemeInstanceExpand}
                onOwnerPillClick={handleOwnerPillClick}
                owners={data.owners || []}
                formatMoney={formatMoney}
                demoMode={true}
              />
            </TabPanel>
          )}

          {data.status !== 'IN_REVIEW' && (
            <TabPanel value={tabValue} index={1}>
              {/* Team Tab */}
              {caseId && <CaseTeam caseId={caseId} headers={headers} />}
            </TabPanel>
          )}

          {data.status !== 'IN_REVIEW' && (
            <TabPanel value={tabValue} index={2}>
              {/* Investigative Tools Tab with sub-tabs */}
              <InvestigativeTools
              caseId={caseId!}
              facilities={sortedFacilities || []}
              facilitiesWithSchemeData={facilitiesWithSchemeData}
              facilitiesData={facilitiesData}
              facilitiesLoading={facilitiesLoading}
              schemeSummaryLoading={investigativeToolsLoading}
              facilitiesSearch={facilitiesSearch}
              facilitiesStateFilter={facilitiesStateFilter}
              facilitiesPage={facilitiesPage}
              facilitiesPageSize={facilitiesPageSize}
              sortField={sortField}
              sortDirection={sortDirection}
              expandedFacilitiesScheme={expandedFacilitiesScheme}
              expandedSchemeInstances={expandedSchemeInstances}
              highlightedOwner={highlightedOwner}
              states={data.summary?.states || []}
              ownershipChangesData={ownershipChangesData}
              onSearchChange={(value) => {
                setFacilitiesSearch(value);
                setFacilitiesPage(0);
              }}
              onStateFilterChange={(value) => {
                setFacilitiesStateFilter(value);
                setFacilitiesPage(0);
              }}
              onPageChange={setFacilitiesPage}
              onPageSizeChange={setFacilitiesPageSize}
              onSort={handleSort}
              onFacilitySchemeExpand={handleFacilitySchemeExpand}
              onSchemeInstanceExpand={handleSchemeInstanceExpand}
              onOwnerPillClick={handleOwnerPillClick}
              onAddFacility={handleAddFacilityDialogOpen}
              onDeleteFacility={handleDeleteFacilityClick}
              owners={data.owners || []}
              expandedOwners={expandedOwners}
              onOwnerExpand={handleOwnerExpand}
              data={data}
              formatMoney={formatMoney}
            />
            </TabPanel>
          )}

          {data.status !== 'IN_REVIEW' && (
            <TabPanel value={tabValue} index={3}>
              {/* Template Documents Tab */}
              {caseId && <TemplateDocuments key={templateDocumentsRefreshKey} caseId={caseId} caseData={data} onDocumentMove={handleDocumentMove} />}
            </TabPanel>
          )}

          {data.status !== 'IN_REVIEW' && (
            <TabPanel value={tabValue} index={4}>
              {/* Case Files Tab */}
              {caseId && (
                <CaseDocuments
                  key={caseDocumentsRefreshKey}
                  caseId={caseId}
                  caseData={data}
                  onDocumentChange={() => setTimelineRefreshKey(prev => prev + 1)}
                  highlightDocumentId={searchParams.get('highlight') || undefined}
                />
              )}
            </TabPanel>
          )}

        </Paper>

        {/* Delete Confirmation Dialog */}
        <DeleteFacilityDialog
          open={deleteConfirmOpen}
          facility={facilityToDelete}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteFacility}
          formatMoney={formatMoney}
        />

        {/* Enhanced Add Facility Modal */}
        <AddFacilityModal
          open={addFacilityDialogOpen}
          onClose={handleAddFacilityDialogClose}
          onAddFacility={handleAddFacility}
          currentFacilities={data.facilities || []}
          authHeaders={headers}
          formatCurrency={formatMoney}
          caseId={caseId || ''}
        />

        {/* Case Log Entry Modal */}
        <Dialog
          open={caseLogModalOpen}
          onClose={handleCloseCaseLogModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotesIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                New Case Log Entry
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 2 }}>
            {/* Log Content Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 2 }}>
                <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Log Content
                </Typography>
              </Box>
              <RichTextEditor
                initialContent={newLogContent}
                onChange={(content) => setNewLogContent(content)}
                placeholder="Enter case log details, updates, or notes..."
                minHeight={150}
                showSaveButton={false}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Date and Tags Section */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <EventIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Log Date
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="date"
                  value={newLogDate}
                  onChange={(e) => setNewLogDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'grey.50',
                    }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <TagIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Tags
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  value={newLogTags}
                  onChange={(e) => setNewLogTags(e.target.value)}
                  placeholder="e.g., follow-up, court filing, meeting"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'grey.50',
                    }
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Attachments Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Attachments
                </Typography>
              </Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                Upload Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewLogFiles([...newLogFiles, ...files]);
                  }}
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                />
              </Button>
              {newLogFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {newLogFiles.length} file{newLogFiles.length !== 1 ? 's' : ''} selected:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {newLogFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        size="small"
                        onDelete={() => setNewLogFiles(newLogFiles.filter((_, i) => i !== index))}
                        icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          maxWidth: 250,
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleCloseCaseLogModal} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleCreateCaseLog}
              variant="contained"
              disabled={!newLogContent.trim()}
              size="large"
              startIcon={<AddIcon />}
            >
              Create Entry
            </Button>
          </DialogActions>
        </Dialog>

        {/* Task Creation Modal */}
        <Dialog
          open={taskModalOpen}
          onClose={handleCloseTaskModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Assign New Task
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 2 }}>
            {/* Task Title Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 2 }}>
                <AssignmentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Task Title
                </Typography>
                <Chip label="Required" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
              <TextField
                fullWidth
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="Enter a clear, concise task title..."
                variant="outlined"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'grey.50',
                  }
                }}
              />
            </Box>

            {/* Task Description Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Description
                </Typography>
              </Box>
              <RichTextEditor
                initialContent={newTaskDescription}
                onChange={(content) => setNewTaskDescription(content)}
                placeholder="Provide detailed instructions or context for this task..."
                minHeight={120}
                showSaveButton={false}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Priority and Assignment Section */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PriorityIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Priority
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined">
                  <Select
                    value={newTaskPriority}
                    onChange={(e) => setNewTaskPriority(e.target.value)}
                    sx={{
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <MenuItem value="low">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        Low
                      </Box>
                    </MenuItem>
                    <MenuItem value="medium">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                        Medium
                      </Box>
                    </MenuItem>
                    <MenuItem value="high">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                        High
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Assign To
                  </Typography>
                </Box>
                <FormControl fullWidth variant="outlined">
                  <Select
                    value={newTaskAssignedTo}
                    onChange={(e) => setNewTaskAssignedTo(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Due Date Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CalendarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Due Date
                </Typography>
              </Box>
              <TextField
                fullWidth
                type="date"
                value={newTaskDueDate}
                onChange={(e) => setNewTaskDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{
                  maxWidth: 300,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'grey.50',
                  }
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Attachments Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Attachments
                </Typography>
              </Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                Upload Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewTaskFiles([...newTaskFiles, ...files]);
                  }}
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                />
              </Button>
              {newTaskFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {newTaskFiles.length} file{newTaskFiles.length !== 1 ? 's' : ''} selected:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {newTaskFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => setNewTaskFiles(newTaskFiles.filter((_, i) => i !== index))}
                        size="small"
                        icon={<UploadIcon sx={{ fontSize: 14 }} />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleCloseTaskModal} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              variant="contained"
              disabled={!newTaskTitle.trim()}
              size="large"
              startIcon={<AddIcon />}
            >
              Create Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* Case Closure Reason Dialog */}
        <Dialog
          open={closureReasonDialogOpen}
          onClose={handleClosureReasonCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Case Closure Reason
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please provide a reason for closing this case. This will be recorded in the case timeline.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={closureReason}
              onChange={(e) => setClosureReason(e.target.value)}
              placeholder="Enter the reason for closing this case..."
              variant="outlined"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'grey.50',
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleClosureReasonCancel} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleClosureReasonSubmit}
              variant="contained"
              disabled={!closureReason.trim()}
              size="large"
              color="error"
            >
              Close Case
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}